import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Accordion, Link as MirandaLink, Select } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Text, Button, Layout } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'
import SelectTruckImage from 'core/ui/assets/truck-info.svg'
import { AnimatedArrowIcon } from 'core/ui/components/animated-arrow-icon/AnimatedArrowIcon'
import { Icon } from 'core/ui/components/icon/Icon'

import { AccordionBodyWrapper } from 'loads/ui/load-setup/LoadSetup.styles'

import { type SelectTractor, useGetCarrierTractorsDatasource } from './hooks/useGetCarrierTractors'
import { useUpdateShipmentTractorNumber } from './hooks/useUpdateShipmentTractorNumber'

const ColoredIconCheck = styled(Icon)`
  color: rgb(${getToken('color-success-100')});
`

const Link = styled(MirandaLink)`
  font-size: ${getToken('font-size-2')};
  align-items: flex-start;
  color: rgb(${getToken('color-text-link')});
`

export type TruckSelectorProps = {
  carrierUUID: string
  shipmentUUID: string
  tractorNumber?: string
  onAssignTruck: () => void
}

export default function TruckSelector({
  carrierUUID,
  shipmentUUID,
  tractorNumber,
  onAssignTruck,
}: TruckSelectorProps) {
  const [isOpened, setIsOpened] = useState(false)
  const [selectedTruck, setSelectedTruck] = useState<SelectTractor>({
    _type: 'tractor',
    value: tractorNumber || '',
    label: tractorNumber || '',
  })
  const tractorsDataSource = useGetCarrierTractorsDatasource(carrierUUID)
  const { updateTractor, requestStatus } = useUpdateShipmentTractorNumber()
  const isSavingTruck = requestStatus === RequestStatus.Pending
  const disableSubmit = !selectedTruck?.value || isSavingTruck

  function handleExpandChange(opened: boolean) {
    setIsOpened(opened)
  }

  function handleAddNewTruck(tractor: string) {
    setSelectedTruck({
      _type: 'tractor',
      value: tractor,
      label: tractor,
      source: 'manual',
    })
    updateTractor({ shipmentUUID, tractorNumber: tractor })
  }

  async function handleAssignTruck() {
    if (!selectedTruck?.value) {
      return
    }

    updateTractor({ shipmentUUID, tractorNumber: selectedTruck.value })
  }

  function handleTruckChange(event: EventLike<Selectable | Selectable[] | null>) {
    setSelectedTruck(event.target.value as SelectTractor)
  }

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      handleExpandChange(false)
      toast.success('Truck assigned with success')
      if (selectedTruck) {
        onAssignTruck()
      }
    }
  }, [onAssignTruck, requestStatus, selectedTruck])

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Failed to assign truck, try again later')
    }
  }, [requestStatus])

  return (
    <Accordion expanded={isOpened} onExpandedChange={handleExpandChange}>
      <Accordion.Toggle
        leading={tractorNumber ? <ColoredIconCheck name="check" width="16" height="16" /> : null}
        trailing={AnimatedArrowIcon}
      >
        <Text variant="heading-sm">Select truck</Text>
        {tractorNumber && <Text variant="heading-sm">- {tractorNumber}</Text>}
      </Accordion.Toggle>
      <AccordionBodyWrapper $overflow={isOpened ? 'initial' : 'auto'}>
        <Accordion.Body>
          <Layout.Group gap="spacing-6">
            <img width={185} src={SelectTruckImage} alt="" style={{ alignSelf: 'center' }} />
            <Layout.Stack align="flex-start">
              <Layout.Stack gap="spacing-1">
                <Text variant="body-md" color="color-text-tertiary">
                  <label htmlFor="truck-select">Tell us which truck will haul this load.</label>
                </Text>
                <Select
                  id="truck-select"
                  name="truck-select"
                  placeholder="Select truck"
                  createOptionPosition="first"
                  onCreate={handleAddNewTruck}
                  datasources={[tractorsDataSource]}
                  disabled={isSavingTruck}
                  onChange={handleTruckChange}
                  value={selectedTruck}
                />
                <Text variant="body-sm">
                  Select a tractor number to track and provide load visibility.
                </Text>
                <Link
                  href="https://na12.voc.project44.com/portal/v2/public/connect/loadsmart"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Setup your project44 integration
                </Link>
              </Layout.Stack>
              <Button
                type="button"
                variant="primary"
                disabled={disableSubmit}
                loading={isSavingTruck}
                onClick={handleAssignTruck}
              >
                Assign truck and save
              </Button>
            </Layout.Stack>
          </Layout.Group>
        </Accordion.Body>
      </AccordionBodyWrapper>
    </Accordion>
  )
}
